import { zodResolver } from "@hookform/resolvers/zod";
import { OptionWithSideElements } from "@jutro/ui/dist/Select/tools";
import { Dayjs } from "dayjs";
import { useForm } from "react-hook-form";
import {
  EdmGetAllDoctorsFromShiftsDocument,
  EdmGetAllNonWorkingPeriodsDocument,
  EdmGetAllStaffWithGenreAndWorkingHoursDocument,
  EdmNonWorkingPeriod,
  useEdmCreateNonWorkingPeriodMutation,
  useEdmDeleteNonWorkingPeriodMutation,
} from "lib/graphql/megaSchema";
import { toaster } from "lib/tools/toaster";
import {
  NonWorkingPeriodSchemaType,
  SlotActionModalMode,
  StaffOption,
  formatTimeToDate,
  getNonWorkingPeriodFormDefaultValues,
  nonWorkingPeriodFormSchema,
} from "views/StaffSchedule/StaffScheduleCreator/tools";

type Props = {
  selectedNonWorkingPeriod: EdmNonWorkingPeriod | undefined;
  startDate: Dayjs;
  selectedDay: Dayjs | undefined;
  selectedBlockStaffId: string;
  staffOptions: OptionWithSideElements<StaffOption>[];
  onCloseModal: () => void;
};

export const useNonWorkingPeriodModalForm = ({
  selectedNonWorkingPeriod,
  startDate,
  selectedDay,
  staffOptions,
  selectedBlockStaffId,
  onCloseModal,
}: Props) => {
  const [createNonWorkingPeriod, { loading: createNonWorkingPeriodLoading }] =
    useEdmCreateNonWorkingPeriodMutation({
      refetchQueries: [
        EdmGetAllNonWorkingPeriodsDocument,
        EdmGetAllDoctorsFromShiftsDocument,
        EdmGetAllStaffWithGenreAndWorkingHoursDocument,
      ],
    });

  const [deleteNonWorkingPeriod, { loading: deleteNonWorkingPeriodLoading }] =
    useEdmDeleteNonWorkingPeriodMutation({
      refetchQueries: [
        EdmGetAllNonWorkingPeriodsDocument,
        EdmGetAllDoctorsFromShiftsDocument,
        EdmGetAllStaffWithGenreAndWorkingHoursDocument,
      ],
    });

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<NonWorkingPeriodSchemaType>({
    mode: "onChange",
    defaultValues: getNonWorkingPeriodFormDefaultValues(
      staffOptions,
      selectedBlockStaffId,
      selectedNonWorkingPeriod,
      selectedDay,
      startDate,
    ),
    resolver: zodResolver(nonWorkingPeriodFormSchema),
  });

  const onSubmit = async (
    formData: NonWorkingPeriodSchemaType,
    mode: SlotActionModalMode,
  ) => {
    if (mode === "delete") {
      if (!selectedNonWorkingPeriod) {
        return;
      }

      const deleteNonWorkingPeriodPromise = deleteNonWorkingPeriod({
        variables: {
          id: selectedNonWorkingPeriod.id,
        },
      });

      await toaster.promise(deleteNonWorkingPeriodPromise, {
        success: "Poprawnie usunięto zmianę",
        loading: "Usuwam zmianę...",
        error: "Nie udało się usunąć zmiany",
      });

      return onCloseModal();
    }

    const { day, timeFrom, timeTo, staff, leaveReason, comment } = formData;

    const createNonWorkingPeriodPromise = createNonWorkingPeriod({
      variables: {
        input: {
          day,
          startFrom: formatTimeToDate(timeFrom, day).toISOString(),
          endedAt: formatTimeToDate(timeTo, day).toISOString(),
          staffId: staff.value.id,
          leaveType: leaveReason.value,
          comment,
        },
      },
    });

    await toaster.promise(createNonWorkingPeriodPromise, {
      success: "Poprawnie utworzono zmianę",
      loading: "Tworzę zmianę...",
      error: "Nie udało się utworzyć zmiany",
    });

    onCloseModal();
  };

  return {
    control,
    errors,
    createNonWorkingPeriodLoading,
    deleteNonWorkingPeriodLoading,
    handleSubmit,
    onSubmit,
  };
};
